import { track } from "@opensea/analytics"
import { useCurrencies } from "@opensea/app-kit"
import { bn } from "@opensea/utils"
import {
  extractChainFromFlowState,
  type ItemWithInitialPricing,
  useFlowEventHandlers,
} from "@opensea/wallet"
import { useEffect } from "react"
import {
  useCoinbaseOneBannerEnabled,
  useIsChestReminderBannerEnabled,
} from "@/hooks/use-flags"
import { Sound, usePlaySound } from "@/hooks/use-play-sound"

function extractTokensInfo(items: ItemWithInitialPricing[]) {
  return items.map(item => ({
    contractAddress: item.contractAddress,
    tokenId: item.tokenId,
    chain: item.chain?.identifier,
    quantity: item.quantity,
  }))
}

export function useFlowTracking() {
  const { addFlowEventHandler } = useFlowEventHandlers()
  const { getUsdPrice } = useCurrencies()

  const [playBuySuccess] = usePlaySound(Sound.BuySuccess)
  const [playListSuccess] = usePlaySound(Sound.ListSuccess)
  const [playSwapSuccess] = usePlaySound(Sound.SwapSuccess)
  const [playAcceptOfferSuccess] = usePlaySound(Sound.AcceptOfferSuccess)
  const [playCreateOfferSuccess] = usePlaySound(Sound.CreateOfferSuccess)
  const [playCancelOrderSuccess] = usePlaySound(Sound.CancelOrderSuccess)
  const [playTransferNftSuccess] = usePlaySound(Sound.TransferNft)
  const [playSendCurrencySuccess] = usePlaySound(Sound.SendCurrencySuccess)
  const [playMint] = usePlaySound(Sound.Mint)

  const coinbaseOneBannerEnabled = useCoinbaseOneBannerEnabled()
  const chestReminderBannerEnabled = useIsChestReminderBannerEnabled()

  useEffect(() => {
    return addFlowEventHandler({
      onError: (flow, errorData) => {
        if (flow.type === "swap" && errorData) {
          const swapReceipt = errorData as {
            fromAsset: {
              symbol: string
              contractAddress: string
              chain: string
              usdPrice: string
            }
            toAsset: {
              symbol: string
              contractAddress: string
              chain: string
              usdPrice: string
            }
            fromQuantity: string
            toQuantity: string
            transactionIdentifier: { hash: string; chain: string }
            failReason?: string
            crossChainRefunded?: boolean
          }
          void track("Swap Error", {
            fromCurrency: {
              symbol: swapReceipt.fromAsset.symbol,
              contractAddress: swapReceipt.fromAsset.contractAddress,
              amount: swapReceipt.fromQuantity,
              chain: swapReceipt.fromAsset.chain,
            },
            toCurrency: {
              symbol: swapReceipt.toAsset.symbol,
              contractAddress: swapReceipt.toAsset.contractAddress,
              amount: swapReceipt.toQuantity,
              chain: swapReceipt.toAsset.chain,
            },
            fromAmount: swapReceipt.fromQuantity,
            toAmount: swapReceipt.toQuantity,
            fromAmountUsd: bn(swapReceipt.fromQuantity)
              .times(swapReceipt.fromAsset.usdPrice)
              .toNumber(),
            toAmountUsd: bn(swapReceipt.toQuantity)
              .times(swapReceipt.toAsset.usdPrice)
              .toNumber(),
            transaction: {
              hash: swapReceipt.transactionIdentifier.hash,
              chain: swapReceipt.transactionIdentifier.chain,
            },
            failReason: swapReceipt.failReason,
            crossChainRefunded: swapReceipt.crossChainRefunded,
            coinbaseOneBannerEnabled,
            chestReminderBannerEnabled,
          })
        }
      },
      onStart: flow => {
        const chain = extractChainFromFlowState(flow)
        void track("Wallet Flow Start", { flow, chain })
        switch (flow.type) {
          case "checkout":
            void track("Purchase Start", {
              itemCount: flow.items.length,
              tokens: extractTokensInfo(flow.items),
            })
            break
          case "accept-offer":
            void track("Offer Accept Start", {
              itemCount: flow.items.length,
            })
            break
          case "listing":
            void track("Listing Start", {
              itemCount: flow.items.length,
            })
            break
          case "create-offer":
            void track("Offer Create Start")
            break
          case "create-collection-offer":
            void track("Collection Offer Create Start")
            break
          case "cancel-order":
            void track("Order Cancel Start")
            break
          case "cancel-all-orders":
            void track("Cancel All Orders Start")
            break
          case "transfer":
            void track("NFT Transfer Start", {
              itemCount: flow.items.length,
            })
            break
          case "swap":
            void track("Swap Start")
            break
          case "connect":
            void track("Connect Start")
            break
          case "siwe":
            void track("SIWE Start")
            break
          case "send":
            void track("ERC20 Transfer Start")
            break
          case "view-wallet":
            void track("View Wallet Start")
            break
          case "mint":
            void track("Mint Start", {
              itemCount: flow.tokenInputs.length,
              crossChain: flow.crossChain,
            })
            break
          default:
            break
        }
      },
      onSuccess: flow => {
        switch (flow.type) {
          case "checkout":
            void track("Purchase Success", {
              itemCount: flow.items.length,
              crossChain: flow.crossChain,
              isSweep: Boolean(flow.slippage),
              transaction: {
                hash: flow.transactionIdentifier?.transactionHash,
                chain: flow.transactionIdentifier?.chain,
              },
              paymentMethod: flow.paymentMethod,
              usdValue: flow.totalUsdPrice,
              tokens: extractTokensInfo(flow.items),
              coinbaseOneBannerEnabled,
              chestReminderBannerEnabled,
            })
            playBuySuccess()
            break
          case "accept-offer":
            void track("Offer Accept Success", {
              itemCount: flow.items.length,
              usdValue: flow.totalUsdPrice,
              transaction: {
                hash: flow.transactionIdentifier?.transactionHash,
                chain: flow.transactionIdentifier?.chain,
              },
            })
            playAcceptOfferSuccess()
            break
          case "listing":
            void track("Listing Success", {
              itemCount: flow.items.length,
            })
            playListSuccess()
            break
          case "create-offer":
            void track("Offer Create Success")
            playCreateOfferSuccess()
            break
          case "create-collection-offer":
            void track("Collection Offer Create Success", {
              trait: flow.trait,
              traits: flow.traits,
            })
            playCreateOfferSuccess()
            break
          case "cancel-order":
            void track("Order Cancel Success")
            playCancelOrderSuccess()
            break
          case "cancel-all-orders":
            void track("Cancel All Orders Success")
            playCancelOrderSuccess()
            break
          case "transfer":
            void track("NFT Transfer Success", {
              itemCount: flow.items.length,
            })
            playTransferNftSuccess()
            break
          case "swap":
            playSwapSuccess()
            break
          case "connect":
            void track("Connect Success")
            break
          case "siwe":
            void track("SIWE Success")
            break
          case "send":
            void track("ERC20 Transfer Success", {
              currency: {
                symbol: flow.currency?.symbol,
                contractAddress: flow.currency?.contractAddress,
                amount: flow.amount,
              },
              amount: flow.amount,
              amountUsd:
                flow.currency && flow.amount
                  ? getUsdPrice(flow.currency, flow.amount)
                  : undefined,
            })
            playSendCurrencySuccess()
            break
          case "view-wallet":
            // No success event for view wallet
            break
          case "mint":
            void track("Mint Success", {
              itemCount: flow.tokenInputs.length,
              crossChain: flow.crossChain,
            })
            playMint()
            break
          default:
            break
        }
      },
      onEnd: flow => {
        switch (flow.type) {
          case "checkout":
            void track("Purchase End", {
              itemCount: flow.items.length,
              crossChain: flow.crossChain,
              tokens: extractTokensInfo(flow.items),
            })
            break
          case "accept-offer":
            void track("Offer Accept End", {
              itemCount: flow.items.length,
            })
            break
          case "listing":
            void track("Listing End", {
              itemCount: flow.items.length,
            })
            break
          case "create-offer":
            void track("Offer Create End")
            break
          case "create-collection-offer":
            void track("Collection Offer Create End")
            break
          case "cancel-order":
            void track("Order Cancel End")
            break
          case "cancel-all-orders":
            void track("Cancel All Orders End")
            break
          case "transfer":
            void track("NFT Transfer End", {
              itemCount: flow.items.length,
            })
            break
          case "swap":
            void track("Swap End")
            break
          case "connect":
            void track("Connect End")
            break
          case "siwe":
            void track("SIWE End")
            break
          case "send":
            void track("ERC20 Transfer End")
            break
          case "view-wallet":
            void track("View Wallet End")
            break
          case "mint":
            void track("Mint End", {
              itemCount: flow.tokenInputs.length,
              crossChain: flow.crossChain,
            })
            break
          default:
            break
        }
      },
    })
  }, [
    addFlowEventHandler,
    getUsdPrice,
    playAcceptOfferSuccess,
    playBuySuccess,
    playCancelOrderSuccess,
    playCreateOfferSuccess,
    playListSuccess,
    playSendCurrencySuccess,
    playSwapSuccess,
    playTransferNftSuccess,
    playMint,
    coinbaseOneBannerEnabled,
    chestReminderBannerEnabled,
  ])
}
